import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import "./FeuturedProjects.css";
import ProjectsData from "../../shared/opensource/projects.json";
import { Fade } from "react-reveal";

class FeaturedProjects extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeCategory: "All",
      showAll: false
    };
  }

  // Get all categories that a project belongs to
  getAllCategoriesForProject = (languages) => {
    if (!languages || languages.length === 0) return ["Other"];
    
    const categories = new Set();
    const languageNames = languages.map(lang => lang.name.toLowerCase().trim());
    
    // Check for Backend languages
    const hasBackend = languageNames.some(lang => 
      ["php", "laravel", "python", "fastapi", "java", "c#", "c++", "node.js"].includes(lang)
    );
    
    // Check for Frontend frameworks/libraries
    const hasFrontendFramework = languageNames.some(lang => 
      ["vue.js", "react", "next.js", "angular", "svelte"].includes(lang)
    );
    
    // Check for basic web technologies
    const hasBasicWeb = languageNames.some(lang => 
      ["javascript", "html", "css", "scss", "tailwind css"].includes(lang)
    );
    
    // Check for AI/ML
    const hasAIML = languageNames.some(lang => 
      ["tensorflow","python", "pytorch", "scikit-learn", "keras", "opencv"].includes(lang)
    );
    
    // Check for Design tools
    const hasDesign = languageNames.some(lang => 
      ["photoshop", "adobe illustrator", "figma", "sketch"].includes(lang)
    );
    
    // Add categories based on technologies
    if (hasBackend && (hasFrontendFramework || hasBasicWeb)) {
      categories.add("Full Stack");
    } else {
      if (hasBackend) categories.add("Backend");
      if (hasFrontendFramework) categories.add("Frontend");
    }
    
    if (hasAIML) categories.add("AI/ML");
    if (hasDesign) categories.add("Design");
    
    // Check for Web (HTML/CSS/JS without frameworks)
    if (hasBasicWeb && !hasFrontendFramework && !hasBackend) {
      categories.add("Web");
    }
    
    // Check for Software (traditional programming languages)
    if (languageNames.some(lang => ["java", "c++", "c#"].includes(lang)) && !hasBackend && !hasFrontendFramework) {
      categories.add("Software");
    }
    
    return categories.size > 0 ? Array.from(categories) : ["Other"];
  }

  // Get unique categories from all projects
  getCategories = () => {
    const allCategories = new Set(["All"]);
    
    ProjectsData.data.forEach(project => {
      const projectCategories = this.getAllCategoriesForProject(project.languages);
      projectCategories.forEach(cat => allCategories.add(cat));
    });
    
    return Array.from(allCategories);
  }

  // Get primary category for display (first category)
  getPrimaryCategory = (languages) => {
    const categories = this.getAllCategoriesForProject(languages);
    return categories[0];
  }

  handleCategoryChange = (category) => {
    this.setState({ activeCategory: category, showAll: false });
  }

  toggleShowAll = () => {
    this.setState({ showAll: !this.state.showAll });
  }

  // Navigate to project detail page
  navigateToProject = (projectId) => {
    this.props.history.push(`/project/${projectId}`);
  }

  getFilteredProjects = () => {
    let filteredProjects = ProjectsData.data;

    if (this.state.activeCategory !== "All") {
      filteredProjects = ProjectsData.data.filter(project => {
        const projectCategories = this.getAllCategoriesForProject(project.languages);
        return projectCategories.includes(this.state.activeCategory);
      });
    }

    return this.state.showAll ? filteredProjects : filteredProjects.slice(0, 6);
  }

  render() {
    const theme = this.props.theme;
    const filteredProjects = this.getFilteredProjects();
    const categories = this.getCategories();
    const hasMoreProjects = this.state.activeCategory === "All"
      ? ProjectsData.data.length > 6
      : ProjectsData.data.filter(project => {
          const projectCategories = this.getAllCategoriesForProject(project.languages);
          return projectCategories.includes(this.state.activeCategory);
        }).length > 6;

    return (
      <div className="featured-projects-main" style={{ backgroundColor: theme.body }}>
        <div className="featured-projects-container">
          {/* Header */}
          <Fade bottom duration={1000}>
            <div className="featured-projects-header">
              <h1 className="featured-projects-title" style={{ color: theme.text }}>
                Featured <span className="title-highlight">Projects</span>
              </h1>
              <p className="featured-projects-subtitle" style={{ color: theme.secondaryText }}>
                Explore my latest work and creative solutions
              </p>
            </div>
          </Fade>

          {/* Category Filters */}
          <Fade bottom duration={1200}>
            <div className="project-categories">
              {categories.map((category, index) => (
                <button
                  key={index}
                  className={`category-btn ${this.state.activeCategory === category ? 'active' : ''}`}
                  onClick={() => this.handleCategoryChange(category)}
                  style={{
                    backgroundColor: this.state.activeCategory === category ? '#10b981' : '#ffffff',
                    color: this.state.activeCategory === category ? 'white' : theme.text,
                    borderColor: this.state.activeCategory === category ? '#10b981' : theme.border
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </Fade>

          {/* Projects Grid */}
          <div className="projects-grid">
            {filteredProjects.map((project, index) => {
              const projectCategories = this.getAllCategoriesForProject(project.languages);
              
              return (
                <Fade key={project.id} bottom duration={1400 + (index * 100)} delay={index * 50}>
                  <div
                    className={`project-card ${index === 0 ? 'featured-main' : ''} ${index === 1 ? 'featured-secondary' : ''}`}
                    style={{
                      backgroundColor: theme.projectCard || '#1f2937',
                      borderColor: theme.border || '#fff',
                      cursor: 'pointer'
                    }}
                    onClick={() => this.navigateToProject(project.id)}
                  >
                    {/* Project Image */}
                    <div className="project-image-container">
                      <div className="project-image">
                        <img
                          src={require(`../../assets/images/${project.path_photo}`)}
                          alt={project.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                            e.target.parentElement.innerHTML += `<div class="project-placeholder">${project.name.charAt(0)}</div>`;
                          }}
                        />
                        <div className="project-overlay">
                          <div className="project-stats">
                            <span className="project-stat">
                              {project.stars || Math.floor(Math.random() * 50)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="project-content">
                      {/* Category Badges */}
                      <div className="project-meta">
                        <div className="project-category-badges">
                          {projectCategories.map((cat, i) => (
                            <span key={i} className="project-category-badge">
                              {cat}
                            </span>
                          ))}
                        </div>
                        <span className="project-date" style={{ color: theme.secondaryText }}>
                          {project.year || '2024'}
                        </span>
                      </div>

                      {/* Project Title */}
                      <h3 className="project-title" >
                        {project.name}
                      </h3>

                      {/* Project Description */}
                      <p className="project-description" style={{ color: theme.secondaryText }}>
                        {project.shortDescription || project.description}
                      </p>

                      {/* Technologies */}
                      <div className="project-technologies">
                        {project.languages.slice(0, 4).map((language, i) => (
                          <span key={i} className="tech-tag" style={{
                            backgroundColor: theme.secondaryBackground || '#fff',
                            color: theme.text
                          }}>
                            {language.name}
                          </span>
                        ))}
                        {project.languages.length > 4 && (
                          <span className="tech-tag more" style={{
                            backgroundColor: theme.secondaryBackground || '#fff',
                            color: theme.secondaryText
                          }}>
                            +{project.languages.length - 4}
                          </span>
                        )}
                      </div>

                      {/* Project Links */}
                      <div className="project-links">
                        <button
                          className="project-link primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.navigateToProject(project.id);
                          }}
                        >
                          <span>View Details</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M7 17L17 7M17 7H7M17 7V17" />
                          </svg>
                        </button>
                        {project.url && project.url !== "private" && (
                          <a
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-link secondary"
                            style={{ color: theme.secondaryText }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span>Live Site</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </Fade>
              );
            })}
          </div>

          {/* Show More/Less Button */}
          {hasMoreProjects && (
            <Fade bottom duration={1600}>
              <div className="show-more-container">
                <button onClick={this.toggleShowAll} className="show-more-btn">
                  {this.state.showAll ? 'Show Less Projects' : 'Show More Projects'}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className={`arrow-icon ${this.state.showAll ? 'rotated' : ''}`}
                  >
                    <path d="M6 9L12 15L18 9" />
                  </svg>
                </button>
              </div>
            </Fade>
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(FeaturedProjects);